import React from 'react';
import styled from 'styled-components';
import { useTranslation } from '../translations';

/**
 * JEDYNA paginacja w aplikacji.
 *
 * Ten komponent istnial juz wczesniej, ale uzywala go DOKLADNIE JEDNA strona (magazyn).
 * Klienci mieli wlasna (9 pozycji na strone), kalendarz wlasna (8), Faktury wlasna (50),
 * a Platnosci nie mialy zadnej — ich lista rosla bez ograniczenia. Stad ujednolicenie:
 * wszystkie listy biora ten komponent, a domyslny rozmiar strony to `DEFAULT_PAGE_SIZE`.
 *
 * Napisy ida przez slownik (wczesniej byly wpisane po polsku na stale, wiec paginacja
 * zostawala polska takze w wersji angielskiej i niemieckiej).
 */

export const DEFAULT_PAGE_SIZE = 25;

const PaginationContainer = styled.nav`
  display: flex;
  gap: var(--sp-2);
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: var(--sp-6);
  padding: var(--sp-5) 0;
`;

const PageButton = styled.button`
  background: ${(p) => (p.$active ? 'var(--primary)' : 'var(--input-bg)')};
  color: ${(p) => (p.$active ? 'var(--primary-contrast)' : 'var(--text)')};
  border: 1px solid ${(p) => (p.$active ? 'transparent' : 'var(--input-border)')};
  border-radius: var(--r-sm);
  padding: var(--sp-2) var(--sp-3);
  cursor: pointer;
  font-size: var(--fs-base);
  font-weight: 600;
  transition: background var(--dur) var(--ease), color var(--dur) var(--ease);
  min-width: 38px;

  &:hover:not(:disabled) {
    background: ${(p) => (p.$active ? 'var(--primary)' : 'var(--hover-bg)')};
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Ellipsis = styled.span`
  color: var(--text-muted);
  font-weight: 600;
  padding: var(--sp-2) var(--sp-1);
  user-select: none;
`;

const PageInfo = styled.span`
  color: var(--text-muted);
  font-size: var(--fs-sm);
  margin-right: var(--sp-3);
  font-weight: 500;
`;

export function getPages(total, current, radius = 2) {
  if (total <= 1) return [1];

  const pages = [1];
  const left = Math.max(2, current - radius);
  const right = Math.min(total - 1, current + radius);

  if (left > 2) pages.push('...');
  for (let p = left; p <= right; p++) pages.push(p);
  if (right < total - 1) pages.push('...');
  pages.push(total);

  return pages;
}

export default function PaginationCompact({
  page,
  totalPages,
  onPageChange,
  pageSize = DEFAULT_PAGE_SIZE,
  totalItems = 0,
  'data-cy': dataCy
}) {
  const { t } = useTranslation();

  if (totalPages <= 1) return null;

  const pages = getPages(totalPages, page);
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);

  return (
    <PaginationContainer aria-label={t('common.pageInfo', { page, total: totalPages })} data-cy={dataCy}>
      <PageInfo>
        {t('common.showingRange', { from: startItem, to: endItem, total: totalItems })}
      </PageInfo>

      <PageButton
        onClick={() => onPageChange(1)}
        disabled={page === 1}
        aria-label={t('common.firstPage')}
      >
        «
      </PageButton>

      <PageButton
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page === 1}
        aria-label={t('common.previousPage')}
      >
        ‹
      </PageButton>

      {pages.map((p, i) => (
        p === '...' ? (
          <Ellipsis key={`ellipsis-${i}`} aria-hidden="true">…</Ellipsis>
        ) : (
          /* `aria-current` zamiast samego koloru: bez niego czytnik ekranu nie wie,
             na ktorej stronie jestesmy — kolor tla nie niesie tej informacji. */
          <PageButton
            key={p}
            $active={p === page}
            aria-current={p === page ? 'page' : undefined}
            onClick={() => onPageChange(p)}
          >
            {p}
          </PageButton>
        )
      ))}

      <PageButton
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        aria-label={t('common.nextPage')}
      >
        ›
      </PageButton>

      <PageButton
        onClick={() => onPageChange(totalPages)}
        disabled={page === totalPages}
        aria-label={t('common.lastPage')}
      >
        »
      </PageButton>
    </PaginationContainer>
  );
}
